/**
 * AuthGoogleButton — "Continue with Google" (Emergent-managed OAuth).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 *
 * Redirect flow:
 *   1. Rider taps this button
 *   2. We stash the current pathname + booking wizard state under
 *      `sessionStorage['link.auth.resume']` so the rider can return
 *      exactly where they left off (Phase C2 spec).
 *   3. We compute the redirect URL LIVE from window.location.origin
 *      and route to https://auth.emergentagent.com/?redirect=<encoded>.
 *   4. Emergent handles Google consent → sends the rider back to
 *      {origin}/auth/google#session_id=<sid>. The <AuthCallback>
 *      component below exchanges that for our JWT and restores the
 *      resume state.
 */
import React from "react";

// Google "G" mark — inline SVG, no vendored asset dependencies.
function GoogleGlyph({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

/**
 * Serialize any resume-worthy state to sessionStorage. Callers can
 * pass a partial `resumeState` (e.g. wizard-in-progress) — pathname
 * is always captured so the rider lands back on the same route.
 */
export function stashResumeState(resumeState) {
  try {
    const payload = {
      pathname: window.location.pathname + window.location.search,
      state: resumeState || null,
      ts: Date.now(),
    };
    sessionStorage.setItem("link.auth.resume", JSON.stringify(payload));
  } catch (_) { /* private-mode / storage full — silently no-op */ }
}

export default function AuthGoogleButton({ resumeState, testId = "auth-google-button", disabled = false, label = "Continue with Google" }) {
  const onClick = () => {
    // 1) preserve booking state
    stashResumeState(resumeState);
    // 2) build the redirect URL LIVE — do NOT hardcode a fallback.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH.
    const redirect = window.location.origin + "/auth/google";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full h-14 bg-white hover:bg-white/95 active:bg-white/90 disabled:opacity-60 text-[#0A1128] rounded-2xl text-[13px] tracking-[0.06em] font-semibold inline-flex items-center justify-center gap-3 border border-black/10 shadow-sm transition-colors"
      data-testid={testId}
    >
      <GoogleGlyph />
      <span>{label}</span>
    </button>
  );
}
